import React, { useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { faAsterisk, faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ActionsMenu from 'components/actions_menu';
import AttachmentIcon from './attachment_icon';
import ClickableIcon from 'components/clickable_icon';
import DeleteModal from './delete_modal';
import ReminderInfo from 'bundles/apex/reminders/info';
import Status from './status';
import Parent from './parent';
import TaskModalStandard from 'bundles/apex/tasks/task_modal/standard';
import Tooltip from 'components/tooltip';
import UserIconList from 'components/user_icon_list';
import { formatDate as formatApexDate } from 'bundles/apex/dates/helpers';
import { formatDate, DAY_MONTH_FORMAT } from 'helpers/formatters/date_time_formats';

import './styles.scss';

export default function Task({
  addTaskAttachment,
  adminedOfficeIds = [],
  apexAdmin,
  attachmentExceptions,
  attachmentsPath,
  canDeleteAttachments,
  checkImmediateRemindersPath,
  currentParent,
  deal,
  deleteTaskAttachment,
  edgeCrm,
  editable = true,
  editing,
  fields,
  hideAssignee,
  options,
  requiredTasksEnabled,
  selected,
  showDealColumn,
  showParents,
  showType,
  task,
  userId,
  users,
  onCreate,
  onDelete,
  onSelectionToggle,
  onUpdate,
}) {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const adminForDeal = apexAdmin || adminedOfficeIds?.includes(deal?.officeId);

  const reminders = task.reminders.filter(reminder => reminder.userId === userId);

  return (
    <React.Fragment>
      {editable &&
        <TaskModalStandard
          checkImmediateRemindersPath={checkImmediateRemindersPath}
          createTask={onCreate}
          edgeCrm={edgeCrm}
          formDependenciesPath={task.formDependenciesPath}
          hideAssignee={hideAssignee}
          options={options}
          reminders={reminders}
          show={showModal}
          showType={showType}
          task={task}
          updateTask={onUpdate}
          users={users?.sort((a, b) => a.label.localeCompare(b.label))}
          onHide={() => setShowModal(false)}
        />
      }

      <DeleteModal
        show={showDeleteModal}
        onDelete={() => onDelete(task)}
        onHide={() => setShowDeleteModal(false)}
      />

      <tr aria-label="task" className="planner-row">
        {editing && (
          <td className="text-center">
            <input
              aria-label="task checkbox"
              checked={selected || false}
              type="checkbox"
              onChange={() => onSelectionToggle(task)}
            />
          </td>
        )}
        <td className="task-complete">
          <Status
            addTaskAttachment={addTaskAttachment}
            attachmentExceptions={attachmentExceptions}
            attachmentsPath={attachmentsPath}
            deleteTaskAttachment={deleteTaskAttachment}
            task={task}
            onUpdate={onUpdate}
          />
        </td>
        <td className={classNames({ 'reminder-info-cell': reminders.length > 0 })}>
          {reminders.length ? (
            <ReminderInfo
              referenceDate={task?.deadline.date}
              reminders={reminders}
              onClick={() => setShowModal(true)}
            />
          ) : <div />}
        </td>
        <td className="planner-table-date">
          <div>
            <div className="d-flex align-items-center">
              {(task.deadline && task.deadline.date)
                ? formatDate(task.deadline.date, DAY_MONTH_FORMAT).toUpperCase()
                : 'TBD'
              }
            </div>
            {task.deadline && !task.deadline.static &&
              <div className="task-deadline-relative mt-1">
                {formatApexDate(deal, fields, task.deadline, false)}
              </div>
            }
          </div>
        </td>
        {showDealColumn &&
          <td>
            <div><a href={`${deal.path}/planner`}>{deal.displayName}</a></div>
          </td>
        }
        <td>
          <div className="d-flex flex-row justify-content-start align-items-center">
            {task.name}
            {task.required &&
              <Tooltip content="Required for voucher submission">
                <FontAwesomeIcon className="ml-2 text-danger text-xs" icon={faAsterisk} />
              </Tooltip>
            }
          </div>
        </td>
        <td>
          <AttachmentIcon
            addTaskAttachment={addTaskAttachment}
            attachmentExceptions={attachmentExceptions}
            attachmentsPath={attachmentsPath}
            canDeleteAttachments={canDeleteAttachments}
            deleteTaskAttachment={deleteTaskAttachment}
            forUserId={userId}
            task={task}
            onUpdate={onUpdate}
          />
        </td>
        <td>
          <div>
            <UserIconList size="30px" users={!hideAssignee && task.assignee ? [task.assignee] : []} />
          </div>
        </td>

        {showParents &&
          <td>
            <Parent
              currentParent={currentParent}
              options={options}
              task={task}
            />
          </td>
        }

        <td>
          <div>
            <ActionsMenu
              disableDelete={requiredTasksEnabled && task.required && !adminForDeal}
              disableDeleteMessage="Required tasks cannot be deleted"
              hideEdit={!editable}
              icon={<ClickableIcon icon={faEllipsisV} />}
              id={task.id}
              onDelete={() => setShowDeleteModal(true)}
              onShowModal={() => setShowModal(true)}
            />
          </div>
        </td>
      </tr>
    </React.Fragment>
  );
}

Task.propTypes = {
  addTaskAttachment: PropTypes.func.isRequired,
  adminedOfficeIds: PropTypes.arrayOf(PropTypes.number),
  apexAdmin: PropTypes.bool,
  attachmentExceptions: PropTypes.bool,
  attachmentsPath: PropTypes.string,
  canDeleteAttachments: PropTypes.bool.isRequired,
  checkImmediateRemindersPath: PropTypes.string,
  currentParent: PropTypes.object,
  deal: PropTypes.object,
  deleteTaskAttachment: PropTypes.func.isRequired,
  edgeCrm: PropTypes.bool,
  editable: PropTypes.bool,
  editing: PropTypes.bool,
  fields: PropTypes.object,
  hideAssignee: PropTypes.bool,
  options: PropTypes.object,
  requiredTasksEnabled: PropTypes.bool,
  selected: PropTypes.bool,
  showDealColumn: PropTypes.bool,
  showParents: PropTypes.bool,
  showType: PropTypes.bool,
  task: PropTypes.shape({
    formDependenciesPath: PropTypes.string,
  }).isRequired,
  userId: PropTypes.number,
  users: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
  })),
  onCreate: PropTypes.func,
  onDelete: PropTypes.func,
  onSelectionToggle: PropTypes.func,
  onUpdate: PropTypes.func,
};
